import { Box, Card, CardMedia, Divider, Grid, Typography } from '@mui/material';
import capitalize from 'lodash/capitalize';
import { InfoField, Link, ImageIcon } from '@occc/ui-design-system';
import {
  MarketingContentFragment,
  Offer_Presentation_Type,
  Offer_Funding_Source,
  TrackingConfig,
} from '@occc/graphql';
import { routeCreator as displayCategoryRouteCreator } from '@occc-app/routes/DisplayCategory/utils';
import { presentationTypeLabels } from '@occc-app/routes/CampaignComponents/components/MarketingContent/consts';
import { useConfig } from '@occc/config';

import { useMemo } from 'react';
import {
  arrangeNodeGroups,
  renderNodeGroups,
} from '@occc-app/routes/CampaignComponents/component-details/sections/utils';
import { SectionCard, ChipList } from '../../components';
import { BLOCKS_IN_ROW, BREAK_URLS_STYLE } from '../constants';
import { formatYesNoValues, formatYesValues } from '../formatters';

type PreviewCardProps = {
  imageUrl?: string | null;
  title?: string | null;
  description?: string | null;
};

const PreviewCard = ({ imageUrl, title, description }: PreviewCardProps) => (
  <Card variant='outlined' elevation={0} sx={{ width: { xs: '100%', sm: 340 }, maxWidth: 340 }}>
    <CardMedia
      sx={({ palette }) => ({
        minHeight: 200,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        backgroundColor: palette.grey[300],
        '& > svg': {
          fontSize: '5rem',
          color: palette.grey[400],
        },
      })}
      image={imageUrl ?? undefined}
    >
      {!imageUrl && <ImageIcon />}
    </CardMedia>
    <Box sx={{ px: '13px', py: '10px' }}>
      <Typography variant='subtitle2' color='text.primary' noWrap>
        {title || 'Title'}
      </Typography>
      <Typography variant='body2' color='text.secondary'>
        {description || 'Description'}
      </Typography>
    </Box>
  </Card>
);

type MarketingContentSectionProps = {
  marketingContent?: MarketingContentFragment | null;
  presentationType?: Offer_Presentation_Type | null;
  presentationPriority?: string | null;
  displayRank?: number;
  displayCategories?: Array<{ name: string; id: string }>;
  category?: string | null;
  fundingSource?: Offer_Funding_Source | null;
  fundingSourceName?: string | null;
  displayForOrderAhead?: boolean | null;
  trackingConfig?: TrackingConfig | null;
};

export const MarketingContentSection = ({
  marketingContent,
  presentationType,
  presentationPriority,
  displayRank,
  displayCategories,
  category,
  fundingSource,
  fundingSourceName,
  displayForOrderAhead,
  trackingConfig,
}: MarketingContentSectionProps) => {
  const isCategoryFieldVisible = useConfig('offerEditor').categoryFieldVisible;

  const displayCategoriesItems = useMemo(
    () =>
      (displayCategories || []).map(({ name, id }) => ({
        label: name,
        href: displayCategoryRouteCreator.details(id),
      })),
    [displayCategories],
  );

  const {
    imageUrl,
    title,
    shortDescription,
    longDescription,
    surgeonGeneralsWarning,
    terms,
    termsUrl,
    disclaimer,
    disclaimerUrl,
    externalUrl,
    receiptContent,
  } = marketingContent || {};

  const topBlockGroups = arrangeNodeGroups(
    [
      <InfoField
        label='Presentation Type'
        value={presentationTypeLabels[presentationType || Offer_Presentation_Type.Default]}
        width='100%'
      />,
      <InfoField
        label='Presentation Priority'
        value={presentationPriority ? capitalize(presentationPriority) : undefined}
        width='100%'
      />,
      <ChipList label='Display Categories' items={displayCategoriesItems} />,
      <InfoField label='Display Rank' value={displayRank} />,
      ...(isCategoryFieldVisible ? [<InfoField label='Category' value={category} />] : []),
    ],
    BLOCKS_IN_ROW,
  );

  const middleBlockGroups = arrangeNodeGroups(
    [
      <InfoField label='Title' value={title} />,
      <InfoField label='Short Description' value={shortDescription} />,
      <InfoField label='Long Description' value={longDescription} />,
      <InfoField label="Surgeon General's Warning" value={surgeonGeneralsWarning} />,
      <InfoField label='Terms and Conditions Text' value={terms} />,
      <InfoField
        label='Terms and Conditions URL'
        value={
          termsUrl ? (
            <Link href={termsUrl} target='_blank' rel='noopener noreferrer' sx={BREAK_URLS_STYLE}>
              {termsUrl}
            </Link>
          ) : undefined
        }
        disableTypography={!!termsUrl}
      />,
      <InfoField label='Disclaimer' value={disclaimer} />,
      <InfoField
        label='Disclaimer URL'
        value={
          disclaimerUrl ? (
            <Link
              href={disclaimerUrl}
              target='_blank'
              rel='noopener noreferrer'
              sx={BREAK_URLS_STYLE}
            >
              {disclaimerUrl}
            </Link>
          ) : undefined
        }
        disableTypography={!!disclaimerUrl}
      />,
    ],
    2,
  );

  const bottomBlockGroups = arrangeNodeGroups(
    [
      <InfoField label='Offer Funding Source' value={fundingSource} />,
      <InfoField label='Funding Source Name' value={fundingSourceName} />,
      <InfoField
        label='Short Description Label Override Text'
        value={receiptContent?.shortDescription}
      />,
      <InfoField
        label='Long Description Label Override Text'
        value={receiptContent?.longDescription}
      />,
      <InfoField
        label='Display for Order Ahead'
        value={formatYesNoValues([displayForOrderAhead])}
      />,
      <InfoField
        label='Marketing Custom Data'
        value={
          marketingContent?.customData && Object.keys(marketingContent.customData).length > 0
            ? formatYesValues([marketingContent.customData])
            : undefined
        }
      />,
      <InfoField
        label='Offer Image URL'
        value={
          imageUrl ? (
            <Link href={imageUrl} target='_blank' rel='noopener noreferrer' sx={BREAK_URLS_STYLE}>
              {imageUrl}
            </Link>
          ) : undefined
        }
        disableTypography={!!imageUrl}
      />,
      <InfoField
        label='External URL'
        value={
          externalUrl ? (
            <Link
              href={externalUrl}
              target='_blank'
              rel='noopener noreferrer'
              sx={BREAK_URLS_STYLE}
            >
              {externalUrl}
            </Link>
          ) : undefined
        }
        disableTypography={!!externalUrl}
      />,
      <InfoField label='Tracking ID' value={trackingConfig?.trackingId} />,
      <InfoField
        label='Include Tracking ID to monthly DDM report'
        value={formatYesNoValues([trackingConfig?.isActivityLogIncludedInAltriaDDM])}
      />,
    ],
    BLOCKS_IN_ROW,
  );

  return (
    <SectionCard title='Marketing Content'>
      {renderNodeGroups(topBlockGroups, BLOCKS_IN_ROW)}

      <Divider />

      <Grid container spacing={3}>
        <Grid
          item
          xs={12}
          lg={8}
          md={6}
          sx={(theme) => ({ display: 'flex', flexDirection: 'column', gap: theme.spacing(3) })}
        >
          {renderNodeGroups(middleBlockGroups, 2)}
        </Grid>
        <Grid item xs={12} lg={4} md={6}>
          <Box display='flex' justifyContent={{ xs: 'center', md: 'flex-start' }}>
            <PreviewCard imageUrl={imageUrl} title={title} description={shortDescription} />
          </Box>
        </Grid>
      </Grid>

      <Divider />

      {renderNodeGroups(bottomBlockGroups, BLOCKS_IN_ROW)}
    </SectionCard>
  );
};
